import React, { useState, useEffect } from "react";
import {
  Plus,
  Package,
  Search,
  AlertCircle,
  CheckCircle,
  Tag,
  Layers,
} from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";

interface SingleProduct {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  type?: string;
}

interface QuantityManagementProps {
  onRefresh: () => void;
}

const QuantityManagement: React.FC<QuantityManagementProps> = ({ onRefresh }) => {
  const [products, setProducts] = useState<SingleProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<SingleProduct | null>(null);
  const [quantityToAdd, setQuantityToAdd] = useState("");
  const [notes, setNotes] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSingleProducts();
  }, []);

  const loadSingleProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/products/single`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setProducts(data.data.products || []);
      }
    } catch (err) {
      console.error("Error loading single products:", err);
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuantity = async () => {
    if (!selectedProduct || !quantityToAdd) {
      setError("Please select a product and enter quantity");
      return;
    }

    const qty = parseInt(quantityToAdd);
    if (isNaN(qty) || qty <= 0) {
      setError("Quantity must be a positive number");
      return;
    }

    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `${API_BASE_URL}/api/products/${selectedProduct.id}/quantity`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quantity: qty,
            notes: notes.trim() || null,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to add quantity");
      }

      const data = await response.json();
      if (data.success) {
        setSuccess(data.message);
        setQuantityToAdd("");
        setNotes("");
        setSelectedProduct(null);
        await loadSingleProducts();
        onRefresh();
      } else {
        throw new Error(data.message || "Failed to add quantity");
      }
    } catch (err) {
      console.error("Error adding quantity:", err);
      setError(err instanceof Error ? err.message : "Failed to add quantity");
    } finally {
      setAdding(false);
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Add Quantity to Products
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Only single products can have quantity added. Bundle products are composed of single products.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-xs text-red-600 hover:text-red-800 mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4 flex items-start">
            <CheckCircle className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-800">{success}</p>
              <button
                onClick={() => setSuccess(null)}
                className="text-xs text-green-600 hover:text-green-800 mt-1"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Product Selection */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Product
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search single products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
              />
            </div>
          </div>

          {/* Product List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e] mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Loading products...</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? "No products found" : "No single products available"}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        selectedProduct?.id === product.id
                          ? "bg-[#f0f9f4] border-l-4 border-[#264a3e]"
                          : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {product.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Current quantity: {product.quantity || 0} • Price: {product.price} RSD
                          </div>
                        </div>
                        {product.type === "single" && (
                          <Tag className="w-4 h-4 text-green-600 ml-2" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add Quantity Form */}
          {selectedProduct && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Add Quantity to: {selectedProduct.name}
                </h3>
                <p className="text-xs text-gray-600">
                  Current quantity: {selectedProduct.quantity || 0}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity to Add <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={quantityToAdd}
                    onChange={(e) => setQuantityToAdd(e.target.value)}
                    placeholder="Enter quantity"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., Supplier batch #123"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProduct(null);
                    setQuantityToAdd("");
                    setNotes("");
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddQuantity}
                  disabled={adding || !quantityToAdd}
                  className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {adding ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Quantity
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default QuantityManagement;
